/**
 * Dónde se guardan las copias automáticas de este dispositivo:
 *
 * - «Dentro de Rumbo»: una base de datos propia del navegador
 *   (`rumbo-copias`, IndexedDB), aparte de la del presupuesto, con las
 *   últimas `MAX_COPIAS_INTERNAS` copias de cada presupuesto.
 * - «En una carpeta»: el permiso para escribir en la carpeta elegida
 *   (File System Access), que también se guarda aquí. Solo existe en los
 *   navegadores que lo permiten; si no, la opción no se ofrece.
 */
import { copiasSobrantes, MAX_COPIAS_INTERNAS } from './automatica';

const BD = 'rumbo-copias';
const COPIAS = 'copias';
const CARPETAS = 'carpetas';

export type CopiaInterna = {
  id: string;
  presupuesto: string;
  /** Fecha y hora ISO de la copia. */
  fecha: string;
  nombre: string;
  datos: Blob;
};

export type ResumenCopia = Omit<CopiaInterna, 'datos'> & { tamano: number };

function abrir(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const peticion = indexedDB.open(BD, 1);
    peticion.onupgradeneeded = () => {
      const bd = peticion.result;
      if (!bd.objectStoreNames.contains(COPIAS)) {
        bd.createObjectStore(COPIAS, { keyPath: 'id' }).createIndex(
          'presupuesto',
          'presupuesto',
        );
      }
      if (!bd.objectStoreNames.contains(CARPETAS)) {
        bd.createObjectStore(CARPETAS);
      }
    };
    peticion.onsuccess = () => resolve(peticion.result);
    peticion.onerror = () => reject(peticion.error);
  });
}

function esperar<T>(peticion: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    peticion.onsuccess = () => resolve(peticion.result);
    peticion.onerror = () => reject(peticion.error);
  });
}

function terminar(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

async function todasDe(
  bd: IDBDatabase,
  presupuesto: string,
): Promise<CopiaInterna[]> {
  const tx = bd.transaction(COPIAS, 'readonly');
  return esperar(
    tx.objectStore(COPIAS).index('presupuesto').getAll(presupuesto),
  ) as Promise<CopiaInterna[]>;
}

/** Guarda una copia y borra las más viejas de ese presupuesto. */
export async function guardarCopiaInterna(
  presupuesto: string,
  nombre: string,
  datos: Blob,
  ahora: Date = new Date(),
): Promise<void> {
  const bd = await abrir();
  try {
    const copia: CopiaInterna = {
      id: `${presupuesto}:${ahora.toISOString()}`,
      presupuesto,
      fecha: ahora.toISOString(),
      nombre,
      datos,
    };
    const tx = bd.transaction(COPIAS, 'readwrite');
    tx.objectStore(COPIAS).put(copia);
    await terminar(tx);

    const sobran = copiasSobrantes(
      await todasDe(bd, presupuesto),
      MAX_COPIAS_INTERNAS,
    );
    if (sobran.length > 0) {
      const borrar = bd.transaction(COPIAS, 'readwrite');
      for (const vieja of sobran) {
        borrar.objectStore(COPIAS).delete(vieja.id);
      }
      await terminar(borrar);
    }
  } finally {
    bd.close();
  }
}

/** Copias guardadas de un presupuesto, la más reciente primero. */
export async function listarCopiasInternas(
  presupuesto: string,
): Promise<ResumenCopia[]> {
  const bd = await abrir();
  try {
    const copias = await todasDe(bd, presupuesto);
    return copias
      .map(({ datos, ...resto }) => ({ ...resto, tamano: datos.size }))
      .sort((a, b) => (a.fecha < b.fecha ? 1 : -1));
  } finally {
    bd.close();
  }
}

export async function leerCopiaInterna(
  id: string,
): Promise<CopiaInterna | null> {
  const bd = await abrir();
  try {
    const tx = bd.transaction(COPIAS, 'readonly');
    return ((await esperar(tx.objectStore(COPIAS).get(id))) ??
      null) as CopiaInterna | null;
  } finally {
    bd.close();
  }
}

/* ── Carpeta elegida (File System Access) ─────────────────────────────── */

/** Lo mínimo de la API de carpetas que se usa (no está en todos los tipos). */
type Permiso = 'granted' | 'denied' | 'prompt';
export type Carpeta = {
  name: string;
  queryPermission?: (o: { mode: 'readwrite' }) => Promise<Permiso>;
  requestPermission?: (o: { mode: 'readwrite' }) => Promise<Permiso>;
  getFileHandle: (
    nombre: string,
    o: { create: boolean },
  ) => Promise<{
    createWritable: () => Promise<{
      write: (datos: Blob) => Promise<void>;
      close: () => Promise<void>;
    }>;
  }>;
};
type VentanaConCarpetas = {
  showDirectoryPicker?: (o?: {
    mode?: 'readwrite';
    id?: string;
  }) => Promise<Carpeta>;
};

/** ¿Este navegador deja elegir una carpeta y escribir en ella? */
export function puedeElegirCarpeta(
  ventana: unknown = typeof window === 'undefined' ? undefined : window,
): boolean {
  return (
    typeof (ventana as VentanaConCarpetas | undefined)?.showDirectoryPicker ===
    'function'
  );
}

/** Abre el selector de carpetas (necesita que la persona toque un botón). */
export async function elegirCarpeta(presupuesto: string): Promise<Carpeta> {
  const ventana = window as unknown as VentanaConCarpetas;
  if (!ventana.showDirectoryPicker) {
    throw new Error('sin-carpetas');
  }
  const carpeta = await ventana.showDirectoryPicker({
    mode: 'readwrite',
    id: 'rumbo-copias',
  });
  const bd = await abrir();
  try {
    const tx = bd.transaction(CARPETAS, 'readwrite');
    tx.objectStore(CARPETAS).put(carpeta, presupuesto);
    await terminar(tx);
  } finally {
    bd.close();
  }
  return carpeta;
}

export async function leerCarpeta(
  presupuesto: string,
): Promise<Carpeta | null> {
  const bd = await abrir();
  try {
    const tx = bd.transaction(CARPETAS, 'readonly');
    return ((await esperar(tx.objectStore(CARPETAS).get(presupuesto))) ??
      null) as Carpeta | null;
  } finally {
    bd.close();
  }
}

/**
 * ¿Se puede escribir ya en la carpeta? Con `pedir` (solo tras un toque) se
 * pide el permiso si el navegador lo ha olvidado.
 */
export async function permisoCarpeta(
  carpeta: Carpeta,
  pedir: boolean,
): Promise<boolean> {
  const opciones = { mode: 'readwrite' as const };
  if ((await carpeta.queryPermission?.(opciones)) === 'granted') {
    return true;
  }
  if (!pedir || !carpeta.requestPermission) {
    return false;
  }
  return (await carpeta.requestPermission(opciones)) === 'granted';
}

export async function escribirEnCarpeta(
  carpeta: Carpeta,
  nombre: string,
  datos: Blob,
): Promise<void> {
  const archivo = await carpeta.getFileHandle(nombre, { create: true });
  const escritura = await archivo.createWritable();
  await escritura.write(datos);
  await escritura.close();
}
